import type { Nullable } from '@/types/globals';

const DEFAULT_DECIMAL_COUNT = 2;
const THOUSANDS_SEPARATOR = ' ';
const DECIMAL_SEPARATOR = '.';

const hasAmount = (amount: Nullable<string | number>): boolean => {
    if (amount === null || amount === undefined) return false;
    if (typeof amount === 'string') {
        return amount.trim().length > 0;
    }
    return true;
};

export const formatMoney = (amount: Nullable<string | number>, decimalCountParam = DEFAULT_DECIMAL_COUNT): string => {
    let decimalCount = Math.abs(decimalCountParam);
    decimalCount = Number.isNaN(decimalCount) ? DEFAULT_DECIMAL_COUNT : decimalCount;

    if (!hasAmount(amount)) {
        return `0.${'0'.repeat(decimalCount)}`;
    }

    const numericAmount = Number(amount);
    const negativeSign = numericAmount < 0 ? '-' : '';
    const normalizedAmount = Math.abs(numericAmount).toFixed(decimalCount);
    const integerPart = parseInt(normalizedAmount, 10).toString();
    const remainderLength = integerPart.length > 3 ? integerPart.length % 3 : 0;
    const thousands = remainderLength ? `${integerPart.substring(0, remainderLength)}${THOUSANDS_SEPARATOR}` : '';
    const rest = integerPart.substring(remainderLength).replace(/(\d{3})(?=\d)/g, `$1${THOUSANDS_SEPARATOR}`);
    const fraction = decimalCount ? `${DECIMAL_SEPARATOR}${normalizedAmount.slice(-decimalCount)}` : '';

    return `${negativeSign}${thousands}${rest}${fraction}`;
};

export const getLocalizedRawAmount = (
    appLanguage = 'en',
    country = 'US',
    amount?: Nullable<string | number>,
    decimalCount = DEFAULT_DECIMAL_COUNT,
    shouldBeInteger = false,
): string | null => {
    const finalDecimalCount = shouldBeInteger && Number.isInteger(Number(amount)) ? 0 : decimalCount;
    const formattedString = formatMoney(amount ?? null, finalDecimalCount);
    return getLocalizedFormattedAmount(appLanguage, country, formattedString);
};

export const getLocalizedFormattedAmount = (appLanguage = 'en', country = 'US', amount?: Nullable<string | number>): string | null => {
    if (amount === undefined || amount === null) {
        return null;
    }

    const amountStr = String(amount).replace(/ /g, '');
    const number = parseFloat(amountStr);
    if (Number.isNaN(number)) return '';

    const decimalMatch = amountStr.match(/\.(\d+)/);
    const decimalDigits = decimalMatch ? decimalMatch[1].length : 0;

    const navigatorLanguage = typeof navigator !== 'undefined' ? navigator.language : undefined;
    const browserLanguage = navigatorLanguage?.split('-')[0] ?? '';
    const locale = appLanguage === browserLanguage ? navigatorLanguage || 'en-US' : `${appLanguage}-${country.toUpperCase()}`;

    return new Intl.NumberFormat(locale, {
        minimumFractionDigits: decimalDigits,
        maximumFractionDigits: decimalDigits,
    }).format(number);
};
